/* Concept A: native, bounded scroll composition; no perpetual animation ticker. */
(() => {
  const clamp = value => Math.min(1, Math.max(0, value));
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  class ScrollComposition extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.active = false;
      this.schedule = () => {
        if (!this.active || this.frame || reduced.matches || this.dataset.motion !== 'true') return;
        this.frame = requestAnimationFrame(() => { this.frame = 0; this.render(); });
      };
      this.reset = () => {
        if (this.frame) cancelAnimationFrame(this.frame);
        this.frame = 0;
        this.classList.toggle('is-static', reduced.matches || this.dataset.motion !== 'true' || !('IntersectionObserver' in window));
        this.clear(); this.schedule();
      };
      window.addEventListener('scroll', this.schedule, {passive:true, signal});
      window.addEventListener('resize', this.reset, {passive:true, signal});
      reduced.addEventListener('change', this.reset, {signal});
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          this.active = entries.some(entry => entry.isIntersecting);
          this.classList.toggle('is-tracking', this.active && !reduced.matches);
          if (this.active) this.schedule();
          else { this.classList.remove('is-tracking'); }
        }, {rootMargin:'100px'});
        this.observer.observe(this);
      }
      this.reset();
      this.dataset.initializations = '1';
    }
    clear() {
      [...this.style].filter(key => key.startsWith('--op-media') || key.startsWith('--op-title') || key.startsWith('--op-caption') || key.startsWith('--op-mobile') || key.startsWith('--op-film')).forEach(key => this.style.removeProperty(key));
      this.querySelector('.op-hero__content')?.removeAttribute('inert');
      this.querySelector('.op-hero-caption')?.removeAttribute('inert');
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null; this.observer?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = 0; this.clear();
    }
  }
  class HeroComposition extends ScrollComposition {
    render() {
      const rect = this.getBoundingClientRect();
      const height = this.querySelector('.op-hero').offsetHeight;
      const desktop = window.innerWidth > 900;
      const progress = clamp(-rect.top / (desktop ? height * .8 : height));
      const ease = progress * progress * (3 - 2 * progress);
      if (desktop) {
        const scale = this.dataset.intensity === 'subtle' ? .64 : .59;
        this.style.setProperty('--op-media-scale', 1 - (1 - scale) * ease);
        this.style.setProperty('--op-media-x', `${window.innerWidth * .06 * ease}px`);
        this.style.setProperty('--op-media-y', `${height * .2 * ease}px`);
        const exit = clamp(progress / .58);
        this.style.setProperty('--op-title-x', `${-window.innerWidth * .09 * exit}px`);
        this.style.setProperty('--op-title-y', `${-height * .15 * exit}px`);
        this.style.setProperty('--op-title-opacity', 1 - exit);
        this.querySelector('.op-hero__content').toggleAttribute('inert', exit >= 1);
        const caption = clamp((progress - .78) / .22);
        this.querySelector('.op-hero-caption').toggleAttribute('inert', caption < .1);
        this.style.setProperty('--op-caption', caption);
        this.style.setProperty('--op-caption-y', `${32 * (1 - caption)}px`);
      } else {
        this.querySelector('.op-hero__content').removeAttribute('inert');
        this.querySelector('.op-hero-caption').removeAttribute('inert');
        this.style.setProperty('--op-mobile-y', `${height * .045 * ease}px`);
        this.style.setProperty('--op-mobile-scale', 1 + .035 * ease);
      }
      this.dataset.progress = progress.toFixed(3);
    }
  }
  class FilmComposition extends ScrollComposition {
    connectedCallback() {
      if (this.dataset.motion !== 'true') { this.classList.add('is-static'); this.dataset.initializations = '1'; return; }
      super.connectedCallback();
    }
    render() {
      const rect = this.getBoundingClientRect();
      const viewport = window.innerHeight;
      // Normal document flow; expansion settles as its centre reaches the viewport centre.
      const progress = clamp((viewport - rect.top) / ((viewport + rect.height) / 2));
      const ease = progress * progress * (3 - 2 * progress);
      const inset = window.innerWidth > 900 ? .84 : 1 - 40 / window.innerWidth;
      this.style.setProperty('--op-film-scale', 1 + (1 / inset - 1) * ease);
      this.style.setProperty('--op-film-type-y', `${24 * (1 - ease)}px`);
      this.dataset.progress = progress.toFixed(3);
    }
  }
  class SignatureLookbook extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.buttons = [...this.querySelectorAll('[data-select]')];
      this.track = this.querySelector('.op-lookbook-slides');
      if (!this.slides.length || !this.track) return;
      this.current = 0;
      this.classList.add('is-enhanced');
      this.querySelectorAll('.op-lookbook-selectors, .op-lookbook-controls').forEach(el => el.hidden = false);
      this.buttons.forEach((button, index) => {
        button.addEventListener('click', () => this.select(index, true), {signal});
        button.addEventListener('pointerenter', event => {
          if (event.pointerType === 'mouse' && matchMedia('(hover:hover) and (pointer:fine)').matches && !this.contains(document.activeElement)) this.select(index);
        }, {signal});
      });
      this.querySelector('[data-prev]').addEventListener('click', () => this.select(this.current - 1, true), {signal});
      this.querySelector('[data-next]').addEventListener('click', () => this.select(this.current + 1, true), {signal});
      this.addEventListener('keydown', event => {
        if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const index = event.key === 'Home' ? 0 : event.key === 'End' ? this.slides.length - 1 : this.current + (event.key === 'ArrowRight' ? 1 : -1);
        this.select(index, true);
      }, {signal});
      this.track.addEventListener('scroll', () => {
        if (window.innerWidth > 900 || this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          const trackRect = this.track.getBoundingClientRect();
          const distances = this.slides.map(slide => Math.abs(slide.getBoundingClientRect().left - trackRect.left - 24));
          this.select(distances.indexOf(Math.min(...distances)));
        });
      }, {passive:true, signal});
      window.addEventListener('resize', () => this.select(this.current, true, true), {signal});
      this.select(0); this.dataset.initializations = '1';
      if ('IntersectionObserver' in window) {
        this.imageObserver = new IntersectionObserver(entries => {
          if (!entries.some(entry => entry.isIntersecting)) return;
          this.querySelectorAll('.op-lookbook-photo img').forEach(image => image.loading = 'eager');
          this.imageObserver.disconnect();
        }, {rootMargin:'600px'});
        this.imageObserver.observe(this);
      }
    }
    select(index, navigate = false, instant = false) {
      index = (index + this.slides.length) % this.slides.length;
      this.current = index;
      this.slides.forEach((slide, i) => {
        const active = i === index;
        slide.classList.toggle('is-active', active);
        slide.toggleAttribute('inert', window.innerWidth > 900 && !active);
        if (window.innerWidth > 900) slide.setAttribute('aria-hidden', String(!active));
        else slide.removeAttribute('aria-hidden');
      });
      this.buttons.forEach((button, i) => button.setAttribute('aria-pressed', String(i === index)));
      const name = this.slides[index].querySelector('h3').textContent;
      const text = `${String(index + 1).padStart(2,'0')} / ${String(this.slides.length).padStart(2,'0')} — ${name}`;
      const status = this.querySelector('.op-lookbook-status');
      if (status.textContent !== text) status.textContent = text;
      if (navigate && window.innerWidth <= 900) {
        this.track.scrollTo({left:this.slides[index].offsetLeft - this.slides[0].offsetLeft, behavior:reduced.matches || this.dataset.motion !== 'true' || instant ? 'auto' : 'smooth'});
      }
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null;
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = 0; this.imageObserver?.disconnect(); this.classList.remove('is-enhanced');
      this.slides?.forEach(slide => { slide.removeAttribute('inert'); slide.removeAttribute('aria-hidden'); });
    }
  }
  let youtubeAPI;
  function loadYouTube() {
    if (window.YT?.Player) return Promise.resolve(window.YT);
    if (youtubeAPI) return youtubeAPI;
    youtubeAPI = new Promise((resolve, reject) => {
      const previous = window.onYouTubeIframeAPIReady;
      const timeout = setTimeout(() => { youtubeAPI = null; reject(new Error('Player unavailable')); }, 12000);
      window.onYouTubeIframeAPIReady = () => { previous?.(); clearTimeout(timeout); resolve(window.YT); };
      const script = document.createElement('script');
      script.src = 'https://www.youtube.com/iframe_api';
      script.onerror = () => { clearTimeout(timeout); youtubeAPI = null; reject(new Error('Player unavailable')); };
      document.head.append(script);
    });
    return youtubeAPI;
  }
  class ExternalFilm extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      const controls = this.closest('opale-film-scroll')?.querySelector('.op-film-external-controls') || this;
      this.button = controls.querySelector('button');
      this.status = controls.querySelector('[role="status"]');
      this.mount = this.querySelector('.op-youtube-mount');
      this.preferences = controls.querySelector('[data-cookie-preferences]');
      this.preferences?.addEventListener('click', () => {
        if (window.privacyBanner?.showPreferences) window.privacyBanner.showPreferences().catch(() => { this.status.textContent = 'Cookie preferences are unavailable here. Use View on YouTube.'; });
        else this.status.textContent = 'Cookie preferences are unavailable here. Use View on YouTube.';
      }, {signal});
      this.button.hidden = false;
      this.inView = false; this.userPaused = false; this.playing = false;
      this.button.addEventListener('click', () => {
        if (this.playing) { this.userPaused = true; this.pause(); }
        else { this.userPaused = false; this.activate(true); }
      }, {signal});
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.pause(); else this.autoplay();
      }, {signal});
      document.addEventListener('visitorConsentCollected', () => {
        if (!this.permitted()) this.destroyPlayer(); else this.autoplay();
      }, {signal});
      reduced.addEventListener('change', () => { this.pause(); if (!reduced.matches) this.autoplay(); }, {signal});
      this.observer = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting;
        if (this.inView) this.autoplay(); else this.pause();
      }, {threshold:.25});
      this.observer.observe(this);
      if (!window.Shopify?.customerPrivacy && window.Shopify?.loadFeatures) {
        window.Shopify.loadFeatures([{name:'consent-tracking-api',version:'0.1'}], error => {
          if (!error && this.isConnected) this.autoplay();
        });
      }
    }
    permitted() {
      // Fail closed if Shopify's privacy API is not available. Never set consent here.
      const privacy = window.Shopify?.customerPrivacy;
      const banner = document.querySelector('#shopify-pc__banner');
      const awaitingChoice = banner && banner.getClientRects().length > 0 && getComputedStyle(banner).visibility !== 'hidden' && getComputedStyle(banner).opacity !== '0';
      const permitted = privacy?.marketingAllowed?.() === true && privacy?.currentVisitorConsent?.().marketing !== 'no' && !awaitingChoice;
      this.dataset.consent = String(permitted);
      return permitted;
    }
    autoplay() {
      if (this.inView && this.dataset.autoplay === 'true' && !this.userPaused && !reduced.matches && !document.hidden) {
        if (this.permitted()) this.activate(false);
        else { this.status.textContent = 'Allow marketing cookies to autoplay this external film.'; if (this.preferences) this.preferences.hidden = false; }
      }
    }
    async activate(explicit) {
      if (!this.permitted()) { this.status.textContent = 'Allow marketing cookies in cookie preferences to play this external film, or use View on YouTube.'; if (this.preferences) this.preferences.hidden = false; return; }
      if (this.player && this.ready) { this.player.playVideo(); return; }
      if (this.loading) return;
      this.loading = true;
      if (this.preferences) this.preferences.hidden = true;
      this.status.textContent = 'Loading external film…';
      const controller = this.controller;
      try {
        const api = await loadYouTube();
        if (controller.signal.aborted || !this.isConnected || !this.permitted() || (!explicit && (!this.inView || reduced.matches || document.hidden))) return;
        const target = document.createElement('div'); this.mount.replaceChildren(target);
        this.player = new api.Player(target, {
          host:'https://www.youtube-nocookie.com', width:'100%', height:'100%', videoId:this.dataset.videoId,
          playerVars:{playsinline:1,controls:0,disablekb:1,rel:0,loop:1,playlist:this.dataset.videoId,origin:location.origin},
          events:{
            onReady: event => {
              if (controller.signal.aborted || !this.permitted()) { event.target.destroy(); return; }
              this.ready = true;
              event.target.getIframe().title = this.dataset.title || 'External YouTube film';
              this.classList.add('is-loaded'); this.status.textContent = '';
              event.target.mute();
              if (!document.hidden && this.inView && (explicit || !reduced.matches)) event.target.playVideo();
              this.playTimeout = setTimeout(() => {
                if (!this.playing && this.isConnected) this.status.textContent = 'Playback may require the YouTube play control. If unavailable, use View on YouTube.';
              }, 5000);
            },
            onStateChange: event => {
              if (controller.signal.aborted) return;
              this.playing = event.data === 1;
              this.dataset.playback = String(event.data);
              this.dataset.muted = String(event.target.isMuted());
              if (event.data === 2 && !this.forcedPause) this.userPaused = true;
              this.forcedPause = false;
              this.button.textContent = this.playing ? 'Pause external film' : 'Play external film';
              this.button.setAttribute('aria-pressed', String(this.playing));
              if (this.playing) { clearTimeout(this.playTimeout); this.status.textContent = ''; if (!this.inView || document.hidden || !this.permitted()) this.pause(); }
            },
            onError: () => { if (controller.signal.aborted) return; this.destroyPlayer(); this.status.textContent = 'This film cannot be embedded here. View the original on YouTube.'; }
          }
        });
      } catch { this.destroyPlayer(); this.status.textContent = 'The external player is unavailable. View the original on YouTube.'; }
      finally { this.loading = false; }
    }
    pause() { if (this.ready) { this.forcedPause = true; this.player?.pauseVideo(); } }
    destroyPlayer() {
      clearTimeout(this.playTimeout); this.player?.destroy(); this.player = null; this.ready = false; this.playing = false;
      this.classList.remove('is-loaded'); this.mount?.replaceChildren();
      if (this.button) { this.button.textContent = 'Play external film'; this.button.setAttribute('aria-pressed','false'); }
    }
    disconnectedCallback() { this.controller?.abort(); this.controller = null; this.observer?.disconnect(); this.destroyPlayer(); }
  }
  let vimeoAPI;
  function loadVimeo() {
    if (window.Vimeo?.Player) return Promise.resolve(window.Vimeo);
    if (vimeoAPI) return vimeoAPI;
    vimeoAPI = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      const timeout = setTimeout(() => { script.remove(); vimeoAPI = null; reject(new Error('Player unavailable')); }, 12000);
      script.src = 'https://player.vimeo.com/api/player.js';
      script.onload = () => { clearTimeout(timeout); resolve(window.Vimeo); };
      script.onerror = () => { clearTimeout(timeout); script.remove(); vimeoAPI = null; reject(new Error('Player unavailable')); };
      document.head.append(script);
    });
    return vimeoAPI;
  }
  class VimeoFilm extends ExternalFilm {
    connectedCallback() {
      if (this.controller) return;
      this.stamp('connected');
      this.dataset.navigationStart = '0';
      this.dataset.blocking = '0'; this.dataset.cls = '0.0000';
      this.metrics = [];
      if ('PerformanceObserver' in window) {
        const observe = (type, callback) => {
          if (!PerformanceObserver.supportedEntryTypes.includes(type)) return;
          const observer = new PerformanceObserver(callback); observer.observe({type,buffered:true}); this.metrics.push(observer);
        };
        observe('largest-contentful-paint', list => { this.dataset.heroLcp = String(Math.round(list.getEntries().at(-1).startTime)); });
        let blocking = 0, shift = 0;
        observe('longtask', list => { list.getEntries().forEach(entry => blocking += Math.max(0,entry.duration-50)); this.dataset.blocking = String(Math.round(blocking)); });
        observe('layout-shift', list => { list.getEntries().forEach(entry => { if (!entry.hadRecentInput) shift += entry.value; }); this.dataset.cls = shift.toFixed(4); });
      }
      this.network = navigator.connection;
      this.criticalReady = document.readyState === 'complete';
      super.connectedCallback();
      // Vimeo continues offscreen; visibility alone never creates or pauses a player.
      this.observer?.disconnect();
      this.observer = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting;
        if (this.inView) this.stamp('viewportEntry');
      });
      this.observer.observe(this);
      const signal = this.controller.signal;
      this.criticalReady = document.readyState === 'complete';
      const release = () => { this.criticalReady = true; this.autoplay(); };
      window.addEventListener('load', release, {once:true, signal});
      document.querySelector('.op-hero img')?.addEventListener('load', () => this.stamp('heroLoaded'), {once:true, signal});
      this.network = navigator.connection;
      this.network?.addEventListener('change', () => { if (this.constrained()) this.pause(); else this.autoplay(); }, {signal});
      this.autoplay();
    }
    stamp(name) { if (!this.dataset[name]) this.dataset[name] = String(Math.round(performance.now())); }
    constrained() { return reduced.matches || this.network?.saveData || /(^|-)2g$/.test(this.network?.effectiveType || ''); }
    autoplay() {
      if (!this.controller || this.dataset.autoplay !== 'true' || this.userPaused || document.hidden) return;
      if (this.constrained()) { this.classList.add('needs-play'); return; }
      if (!this.permitted()) { this.status.textContent = 'Allow marketing cookies to play this external film.'; this.preferences.hidden = false; return; }
      this.stamp('consentAvailable');
      if (this.dataset.startup === 'immediate' || this.criticalReady) this.activate(false);
    }
    disconnectedCallback() { this.metrics?.forEach(observer => observer.disconnect()); super.disconnectedCallback(); }
    async activate(explicit) {
      if (!this.permitted()) { this.status.textContent = 'Allow marketing cookies to play this external film.'; this.preferences.hidden = false; return; }
      if (this.loading) return;
      if (this.ready) { if (!this.playing) await this.play(); return; }
      this.loading = true; this.preferences.hidden = true;
      this.status.textContent = 'Loading film…';
      const controller = this.controller;
      try {
        if (!document.querySelector('link[data-opale-vimeo-connect]')) {
          const connection = document.createElement('link'); connection.rel = 'preconnect'; connection.href = 'https://player.vimeo.com'; connection.setAttribute('data-opale-vimeo-connect',''); document.head.append(connection);
        }
        this.stamp('sdkRequest');
        const api = await loadVimeo();
        if (controller.signal.aborted || !this.isConnected || !this.permitted() || (!explicit && (this.constrained() || document.hidden))) return;
        const iframe = document.createElement('iframe');
        const url = new URL(`https://player.vimeo.com/video/${this.dataset.videoId}`);
        Object.entries({background:1,autoplay:0,muted:1,loop:1,controls:0,title:0,byline:0,portrait:0,playsinline:1,dnt:1}).forEach(([key,value]) => url.searchParams.set(key,value));
        iframe.src = url.toString(); iframe.title = this.dataset.title || 'Background fashion film';
        iframe.allow = 'autoplay; fullscreen; picture-in-picture'; iframe.referrerPolicy = 'strict-origin-when-cross-origin';
        this.stamp('iframeCreated');
        this.mount.replaceChildren(iframe);
        const player = new api.Player(iframe); this.player = player;
        player.on('play', () => {
          if (controller.signal.aborted) return;
          this.playing = true; this.dataset.playback = 'playing'; this.button.textContent = 'Pause film';
          this.button.setAttribute('aria-label','Pause background film'); this.button.setAttribute('aria-pressed','true');
          this.stamp('playEvent'); this.classList.remove('needs-play'); this.status.textContent = '';
          if (document.hidden || !this.permitted()) this.pause();
        });
        player.on('timeupdate', event => {
          this.dataset.currentTime = String(event.seconds);
          if (event.seconds > 0) { this.stamp('playbackProgress'); this.classList.add('is-loaded'); }
        });
        player.on('pause', () => {
          if (controller.signal.aborted) return;
          this.playing = false; this.dataset.playback = 'paused';
          if (!this.forcedPause) this.userPaused = true;
          this.forcedPause = false; this.button.textContent = 'Play film';
          this.button.setAttribute('aria-label','Play background film'); this.button.setAttribute('aria-pressed','false');
        });
        player.on('error', () => { if (!controller.signal.aborted) this.fail(); });
        await player.ready();
        if (controller.signal.aborted || !this.permitted()) { await player.destroy(); return; }
        this.ready = true; this.stamp('playerReady');
        await player.setMuted(true); await player.setVolume(0);
        await player.disableTextTrack().catch(() => {});
        this.dataset.muted = String(await player.getMuted());
        if (!document.hidden && !this.userPaused && (explicit || !this.constrained())) await this.play();
      } catch { if (!controller.signal.aborted) this.fail(); }
      finally { this.loading = false; }
    }
    async play() {
      try { this.stamp('playRequest'); await this.player.play(); this.stamp('playResolved'); }
      catch { this.classList.add('needs-play'); this.dataset.autoplayBlocked = 'true'; this.status.textContent = 'Autoplay was blocked. Use Play film, or open the source.'; }
    }
    pause() { if (this.ready) { this.forcedPause = true; this.player?.pause().catch(() => {}); } }
    fail() { this.classList.add('needs-play'); this.destroyPlayer(); this.status.textContent = 'The Vimeo film is unavailable here. View the original source.'; }
    destroyPlayer() {
      this.player?.destroy().catch(() => {}); this.player = null; this.ready = false; this.playing = false; this.dataset.playback = 'stopped'; delete this.dataset.currentTime;
      this.classList.remove('is-loaded'); this.mount?.replaceChildren();
      if (this.button) { this.button.textContent = 'Play film'; this.button.setAttribute('aria-label','Play background film'); this.button.setAttribute('aria-pressed','false'); }
    }
  }
  [['opale-hero-scroll',HeroComposition],['opale-film-scroll',FilmComposition],['opale-lookbook',SignatureLookbook],['opale-youtube',ExternalFilm],['opale-vimeo',VimeoFilm]].forEach(([name, element]) => {
    if (!customElements.get(name)) customElements.define(name, element);
  });
})();
// Read-only QA telemetry on local preview only. No remote collection or hosted tracking.
if (location.hostname === '127.0.0.1' && 'PerformanceObserver' in window) {
  const supported = PerformanceObserver.supportedEntryTypes || [];
  const totals = () => {
    const resources = performance.getEntriesByType('resource');
    document.documentElement.dataset.opaleExternalRequests = String(resources.filter(entry => new URL(entry.name).hostname !== location.hostname).length);
    document.documentElement.dataset.opaleVimeoRequests = String(resources.filter(entry => /vimeo|vimeocdn/.test(new URL(entry.name).hostname)).length);
    document.documentElement.dataset.opaleYoutubeRequests = String(resources.filter(entry => /youtube|ytimg|googlevideo/.test(new URL(entry.name).hostname)).length);
  };
  if (supported.includes('resource')) new PerformanceObserver(totals).observe({type:'resource',buffered:true});
  if (supported.includes('event')) {
    let maximum = 0;
    new PerformanceObserver(list => {
      list.getEntries().forEach(entry => { if (entry.interactionId) maximum = Math.max(maximum, entry.duration); });
      document.documentElement.dataset.opaleMaxInteraction = String(maximum);
    }).observe({type:'event',buffered:true,durationThreshold:16});
  }
}
